import { useRef, useState } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import emailjs from '@emailjs/browser';
import { FaPhone, FaWhatsapp, FaEnvelope, FaMapMarkerAlt, FaCheckCircle, FaExclamationTriangle, FaSpinner } from 'react-icons/fa';
import logo from '../assets/logo.png';
import contactBg from '../assets/images/contact-bg.jpg';
import './Contact.css';

const EMAILJS_SERVICE  = 'service_pxrrhc5';
const EMAILJS_TEMPLATE = 'template_psmf2hi';
const EMAILJS_KEY      = 'iok3MQKusieJGivnn';
const WA_SUCCESS       = 'https://wa.me/918605256832';

const SERVICES = [
  'Business Registration',
  'Proprietorship Registration',
  'Partnership Firm',
  'LLP Registration',
  'Private Limited Company',
  'Udyam (MSME) Registration',
  'Shop Act License',
  'FSSAI Registration',
  'Trade License',
  'GST Registration',
  'GST Return Filing',
  'Income Tax Filing (ITR)',
  'TDS Filing',
  'Bookkeeping & Accounting',
  'Static Business Website',
  'Dynamic Business Website',
  'E-commerce Website',
  'Social Media Marketing',
  'Google Ads / Meta Ads',
  'Logo & Branding Design',
  'SEO Services',
  'Business Consulting',
  'Financial Modelling',
  'Other',
];

const initialForm = { name: '', phone: '', email: '', service: '', message: '' };

export default function Contact() {
  const ref     = useRef(null);
  const inView  = useInView(ref, { once: true, margin: '-60px' });
  const [form,      setForm]      = useState(initialForm);
  const [status,    setStatus]    = useState('idle'); // idle | sending | success | error
  const [errors,    setErrors]    = useState({});

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Name is required';
    if (!form.phone.trim() || !/^\d{10}$/.test(form.phone.replace(/\s/g, '')))
      e.phone = 'Enter a valid 10-digit phone number';
    return e;
  };

  const handleChange = (ev) => {
    const { name, value } = ev.target;
    setForm((p) => ({ ...p, [name]: value }));
    if (errors[name]) setErrors((p) => ({ ...p, [name]: '' }));
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }

    setStatus('sending');

    try {
      await emailjs.send(
        EMAILJS_SERVICE,
        EMAILJS_TEMPLATE,
        {
          from_name:  form.name,
          phone:      form.phone,
          from_email: form.email,
          service:    form.service || 'Not specified',
          message:    form.message || 'No message provided',
          to_email:   'ravindrarpuri2022@gmail.com',
        },
        EMAILJS_KEY
      );

      setStatus('success');
      setForm(initialForm);
      // Open WhatsApp after short delay so user sees the success message first
      setTimeout(() => window.open(WA_SUCCESS, '_blank'), 1500);

    } catch {
      setStatus('error');
    }
  };

  return (
    <section className="section contact" id="contact">
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
        >
          <p className="section-label">Reach Out</p>
          <h2 className="section-title">Get In Touch</h2>
          <div className="gold-divider" />
          <p className="section-subtitle">We'd love to hear from you. Our team responds within 24 hours.</p>
        </motion.div>

        <div className="contact__body" ref={ref}>
          {/* Left — info */}
          <motion.div
            className="contact__info"
            style={{ backgroundImage: `url(${contactBg})` }}
            initial={{ opacity: 0, x: -40 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <img src={logo} alt="RRP & Associates" className="contact__info-logo" />
            <h3 className="contact__info-title">Contact Information</h3>
            <p className="contact__info-sub">
              Ready to start? Reach us through any of these channels.
            </p>

            <div className="contact__info-items">
              <a href="tel:+919422611832" className="contact__info-item">
                <div className="contact__info-icon"><FaPhone /></div>
                <div>
                  <strong>Phone</strong>
                  <span>+91-9422611832</span>
                </div>
              </a>

              <a
                href="https://wa.me/918605246832?text=Hi%2C%20I%20want%20to%20start%20my%20business"
                target="_blank"
                rel="noopener noreferrer"
                className="contact__info-item"
              >
                <div className="contact__info-icon contact__info-icon--wa"><FaWhatsapp /></div>
                <div>
                  <strong>WhatsApp</strong>
                  <span>Chat with us instantly</span>
                </div>
              </a>

              <a href="mailto:ravindrarpuri2022@gmail.com" className="contact__info-item">
                <div className="contact__info-icon"><FaEnvelope /></div>
                <div>
                  <strong>Email</strong>
                  <span>ravindrarpuri2022@gmail.com</span>
                </div>
              </a>

              <div className="contact__info-item">
                <div className="contact__info-icon"><FaMapMarkerAlt /></div>
                <div>
                  <strong>Location</strong>
                  <span>Pune, Maharashtra, India</span>
                </div>
              </div>
            </div>

            <div className="contact__badge">
              <FaCheckCircle className="contact__badge-icon" />
              <span>We'll respond within <strong>24 hours</strong></span>
            </div>
          </motion.div>

          {/* Right — form */}
          <motion.div
            className="contact__form-wrap"
            initial={{ opacity: 0, x: 40 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <AnimatePresence mode="wait">

              {/* ── Success state ── */}
              {status === 'success' && (
                <motion.div
                  key="success"
                  className="contact__success"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.4 }}
                >
                  <FaCheckCircle className="contact__success-icon" />
                  <h3>Thank you!</h3>
                  <p>We will contact you within few hours 🎉</p>
                  <p className="contact__success-wa">Opening WhatsApp for you…</p>
                  <button
                    className="btn-primary"
                    onClick={() => setStatus('idle')}
                    style={{ marginTop: 20 }}
                  >
                    Send Another Enquiry
                  </button>
                </motion.div>
              )}

              {/* ── Error state ── */}
              {status === 'error' && (
                <motion.div
                  key="error"
                  className="contact__error"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.4 }}
                >
                  <FaExclamationTriangle className="contact__error-icon" />
                  <h3>Something went wrong.</h3>
                  <p>Please WhatsApp us directly.</p>
                  <a
                    href={WA_SUCCESS}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary"
                    style={{ marginTop: 20 }}
                  >
                    <FaWhatsapp /> WhatsApp Us Now
                  </a>
                  <button
                    className="contact__retry-btn"
                    onClick={() => setStatus('idle')}
                  >
                    Try Again
                  </button>
                </motion.div>
              )}

              {/* ── Form (idle + sending) ── */}
              {(status === 'idle' || status === 'sending') && (
                <motion.form
                  key="form"
                  className="contact__form"
                  onSubmit={handleSubmit}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  noValidate
                >
                  <div className="form-row">
                    <div className={`form-group${errors.name ? ' form-group--error' : ''}`}>
                      <label htmlFor="name">Full Name *</label>
                      <input
                        id="name"
                        name="name"
                        type="text"
                        placeholder="Your full name"
                        value={form.name}
                        onChange={handleChange}
                        autoComplete="name"
                        disabled={status === 'sending'}
                      />
                      {errors.name && <span className="form-error">{errors.name}</span>}
                    </div>
                    <div className={`form-group${errors.phone ? ' form-group--error' : ''}`}>
                      <label htmlFor="phone">Phone Number *</label>
                      <input
                        id="phone"
                        name="phone"
                        type="tel"
                        placeholder="10-digit mobile number"
                        value={form.phone}
                        onChange={handleChange}
                        autoComplete="tel"
                        maxLength={10}
                        disabled={status === 'sending'}
                      />
                      {errors.phone && <span className="form-error">{errors.phone}</span>}
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="email">Email Address</label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="your@email.com"
                      value={form.email}
                      onChange={handleChange}
                      autoComplete="email"
                      disabled={status === 'sending'}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="service">Service Needed</label>
                    <select
                      id="service"
                      name="service"
                      value={form.service}
                      onChange={handleChange}
                      disabled={status === 'sending'}
                    >
                      <option value="">Select a service…</option>
                      {SERVICES.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="message">Message</label>
                    <textarea
                      id="message"
                      name="message"
                      rows={4}
                      placeholder="Tell us about your business and requirements…"
                      value={form.message}
                      onChange={handleChange}
                      disabled={status === 'sending'}
                    />
                  </div>

                  <motion.button
                    type="submit"
                    className="btn-primary contact__submit"
                    disabled={status === 'sending'}
                    whileHover={status !== 'sending' ? { scale: 1.02, boxShadow: '0 6px 24px rgba(201,168,76,0.4)' } : {}}
                    whileTap={status !== 'sending' ? { scale: 0.98 } : {}}
                  >
                    {status === 'sending' ? (
                      <><FaSpinner className="contact__spinner" /> Sending…</>
                    ) : (
                      'Send Enquiry →'
                    )}
                  </motion.button>
                </motion.form>
              )}

            </AnimatePresence>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
